'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';
import { createPageviewQueue } from '@/lib/analytics/deferred-pageviews';
import {
  DECISION_RETURN_MAX_MS,
  DECISION_RETURN_MIN_MS,
  RETURN_VISIT_BANNER_EVENT,
  buildReturnVisitBannerPayload,
  getDecisionTarget,
  persistReturnVisitBannerPayload,
  readDecisionStored,
  writeDecisionStored,
  type ReturnVisitBannerPayload,
} from '@/lib/analytics/decision-return-visit';

/**
 * Rutas "transaccionales": las que actualizan query string vía router.replace
 * (filtros, fechas, etc.) sin que sea una navegación nueva del usuario. Para
 * estas rutas dedupeamos $pageview por pathname (no por pathname+query) para
 * evitar inflar pageviews cada vez que el usuario cambia un filtro.
 *
 * El primer pageview SÍ se captura con la URL completa (incluye gclid/UTMs),
 * solo los subsiguientes con query distinta se silencian.
 */
const TRANSACTIONAL_PATHS: ReadonlyArray<string> = ['/reservas'];

/**
 * Lista de parámetros de atribución que SÍ queremos en `$pageview`.
 * Hoy PostHog ve `gclid` en `$autocapture`/`$web_vitals` pero el `$pageview`
 * manual no los lleva, lo que rompe el funnel "click de Ads → resultado".
 *
 * Si Google Ads agrega tracking templates con UTMs (recomendado), capturamos
 * la atribución en el primer evento del visitante.
 */
const ATTRIBUTION_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
  'fbclid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/**
 * Lee la cookie first-party `pacartagena_attribution` (set por el proxy Edge
 * en el primer hit con gclid/utm). Devuelve el objeto parseado o null si no
 * existe / está corrupta. Se ejecuta en cliente, robusto a SSR.
 */
function readAttributionCookie(): Record<string, string> | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )pacartagena_attribution=([^;]+)/);
  if (!match) return null;
  try {
    const decoded = decodeURIComponent(match[1]);
    const parsed = JSON.parse(decoded);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

function PageviewTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPageviewKeyRef = useRef<string | null>(null);
  // Dedup específico para rutas transaccionales (e.g. /reservas): una vez
  // capturado el primer $pageview con URL completa (gclid/UTMs incluidos),
  // ignoramos cambios de query string del mismo pathname (router.replace
  // de filtros). En otras rutas seguimos dedupeando por pathname+search.
  const capturedTransactionalPathRef = useRef<string | null>(null);
  // Pathname del último run del effect — lo usamos para detectar navegación
  // SPA real (cambio de pathname) vs cambios de solo searchParams. Cuando
  // hay navegación real, reseteamos el dedup transaccional para que volver
  // a /reservas tras salir vuelva a emitir $pageview.
  const previousPathnameRef = useRef<string | null>(null);
  const queueRef = useRef<ReturnType<typeof createPageviewQueue> | null>(null);

  useEffect(() => {
    queueRef.current = createPageviewQueue();
    // StrictMode puede montar de nuevo después de cancelar una cola pendiente.
    lastPageviewKeyRef.current = null;
    capturedTransactionalPathRef.current = null;
    return () => queueRef.current?.dispose();
  }, []);

  useEffect(() => {
    if (!pathname) return;
    const qs = searchParams?.toString() ?? '';
    const pageviewKey = qs ? `${pathname}?${qs}` : pathname;
    const isTransactional = TRANSACTIONAL_PATHS.includes(pathname);
    const observedAt = new Date().toISOString();
    const observedReferrer = document.referrer;
    const rawParams = new URLSearchParams(window.location.search);
    const cookieAttribution = readAttributionCookie();

    // Detectar cambio de pathname (navegación SPA real). Si el usuario salió
    // de la ruta transaccional o entró a una nueva, limpiamos el dedup para
    // permitir captura del próximo primer pageview de cualquier ruta.
    if (previousPathnameRef.current !== pathname) {
      capturedTransactionalPathRef.current = null;
      previousPathnameRef.current = pathname;
    }

    // Dedupe transaccional: en /reservas el primer pageview captura URL
    // completa con atribución; los siguientes (por router.replace al cambiar
    // filtros) no emiten un $pageview nuevo. Para resto del sitio, dedupe
    // estándar por pathname+search.
    if (isTransactional && capturedTransactionalPathRef.current === pathname) return;
    if (!isTransactional && lastPageviewKeyRef.current === pageviewKey) return;

    // Reservar el dedup al encolar, no al cargar el SDK. La cola mantiene orden
    // y sobrevive a cambios de ruta; solo se cancela al desmontar el tracker.
    lastPageviewKeyRef.current = pageviewKey;
    if (isTransactional) capturedTransactionalPathRef.current = pathname;
    queueRef.current?.enqueue((posthog) => {

      let url = window.location.origin + pathname;
      if (qs) url += `?${qs}`;

      // Capturar atribución cuando viene en la URL (primary path) y como fallback
      // leer del raw `window.location.search` por si Next.js limpia la URL antes
      // de que llegue a `useSearchParams` (e.g. router.replace en alguna landing).
      const eventProps: Record<string, string> = {
        $current_url: url,
        $pathname: pathname,
        $referrer: observedReferrer,
        $referring_domain: observedReferrer ? new URL(observedReferrer).hostname : '$direct',
        pageview_observed_at: observedAt,
      };
      const attributionProps: Record<string, string> = {};

      for (const key of ATTRIBUTION_PARAMS) {
        const value = searchParams?.get(key) ?? rawParams?.get(key);
        if (value) {
          eventProps[key] = value;
          attributionProps[key] = value;
        }
      }

      // Leer cookie pacartagena_attribution (set por proxy Edge en primer hit).
      // Esto cubre el caso donde Next.js/router limpia query string ANTES de que
      // PostHog SDK termine init — el proxy capturó la atribución server-side
      // y la dejó en cookie first-party.
      if (cookieAttribution) {
        for (const [k, v] of Object.entries(cookieAttribution)) {
          if (typeof v === 'string' && v && !attributionProps[k]) {
            eventProps[k] = v;
            attributionProps[k] = v;
          }
        }
      }

      // Persistir atribución como super-properties de SESIÓN para que eventos
      // posteriores hereden gclid/UTMs y permitan funnel forense por click de Ads.
      if (Object.keys(attributionProps).length > 0) {
        posthog.register_for_session?.({
          ...attributionProps,
          ads_attribution_landing_path: pathname,
          ads_attribution_seen_at: new Date().toISOString(),
        });
      }

      posthog.capture('$pageview', eventProps);

      // ───────────────────────────────────────────────────────────────────
      // Detección de visitante recurrente decisional.
      //
      // Si el visitante ya pasó por este mismo "target" entre 30 min y 14
      // días atrás, emitimos `decision_return_visit_detected`. NO mandamos
      // alerta a humanos — solo marcamos señal en PostHog para análisis de
      // funnel. La ventana descarta scrolling rápido (<30 min) y memoria
      // fría (>14 días). El dedup ya está cubierto por el guard de $pageview
      // arriba: este bloque solo corre cuando se captura un pageview real,
      // así que no hay riesgo de doble disparo por router.replace.
      // ───────────────────────────────────────────────────────────────────
      if (typeof window !== 'undefined') {
        const target = getDecisionTarget(pathname, searchParams);
        if (target) {
          const stored = readDecisionStored(target.key);
          const nowIso = new Date().toISOString();
          const nextCount = (stored?.count ?? 0) + 1;

          if (stored?.lastSeenAt) {
            const prevMs = Date.parse(stored.lastSeenAt);
            if (!Number.isNaN(prevMs)) {
              const diffMs = Date.now() - prevMs;
              if (diffMs >= DECISION_RETURN_MIN_MS && diffMs <= DECISION_RETURN_MAX_MS) {
                const bannerPayload = buildReturnVisitBannerPayload(
                  target,
                  stored,
                  nextCount,
                  new Date(),
                  diffMs
                );
                posthog.capture('decision_return_visit_detected', {
                  target_path: target.path,
                  target_type: target.type,
                  visit_count: nextCount,
                  previous_seen_at: stored.lastSeenAt,
                  days_since_previous: bannerPayload.daysSincePrevious,
                  page_path: pathname,
                });
                persistReturnVisitBannerPayload(bannerPayload);
                window.dispatchEvent(
                  new CustomEvent<ReturnVisitBannerPayload>(RETURN_VISIT_BANNER_EVENT, {
                    detail: bannerPayload,
                  })
                );
              }
            }
          }

          writeDecisionStored(target.key, { count: nextCount, lastSeenAt: nowIso });
        }
      }
    });
  }, [pathname, searchParams]);

  return null;
}

export function PageviewTracker() {
  return (
    <Suspense fallback={null}>
      <PageviewTrackerInner />
    </Suspense>
  );
}
